/**
 * Vessel Service
 * Service for fetching vessel data from API
 */

import { ENDPOINTS } from '@/feat/api-service-layer/endpoints';
import { BASE_URL } from '@/feat/api-service-layer/fetchClient';
import { formatForAPIDate, getUnixTimestamp, now, subtractTime } from '@/utils/date';

export interface VesselPosition {
  timestamp_utc: string;
  longitude: number;
  latitude: number;
  mmsi: number;
  vessel_type: number;
  [key: string]: unknown;
}

export interface VesselResponse {
  result?: VesselPosition[];
  results?: VesselPosition[];
  last_token?: string;
}

export interface VesselPage {
  vessels: VesselPosition[];
  last_token?: string;
}

/**
 * Fetch a single page of vessels.
 * Designed for incremental loading via React Query's useInfiniteQuery.
 *
 * @param getToken - Function to get authentication token
 * @param lastToken - Optional last token for pagination
 * @returns Promise resolving to a page of vessels and the next last_token (if any)
 */
export async function fetchVesselPage(getToken: () => Promise<string>, lastToken?: string): Promise<VesselPage> {
  const token = await getToken();
  const url = `${BASE_URL}${ENDPOINTS.vesselPosition}${lastToken ? `?last_token=${lastToken}` : ''}`;

  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch vessels: ${res.status}`);
  }

  const data: VesselResponse = await res.json();
  const vessels = data.result || data.results || [];

  return {
    vessels,
    last_token: data.last_token,
  };
}

/**
 * Fetch all vessels by walking pages sequentially.
 * Retained for backward compatibility, but new code should prefer fetchVesselPage
 * with React Query's useInfiniteQuery for incremental rendering.
 *
 * @param getToken - Function to get authentication token
 * @param lastToken - Optional last token for pagination
 * @param accumulatedVessels - Accumulated vessels from previous calls
 * @param page - Current page index
 * @returns Promise resolving to array of vessels
 */
export async function fetchVessels(
  getToken: () => Promise<string>,
  lastToken?: string,
  accumulatedVessels: VesselPosition[] = [],
  page: number = 0,
): Promise<VesselPosition[]> {
  const { vessels, last_token } = await fetchVesselPage(getToken, lastToken);
  const allVessels = [...accumulatedVessels, ...vessels];

  // Stop conditions to prevent infinite pagination loops:
  // 1) Fewer than 5000 results returned
  // 2) No nextLastToken from API
  // 3) Same token as current page's request token (API bug / stuck token)
  // 4) Reasonable hard limit on number of pages to avoid runaway loops
  // NOTE: Keep this small to avoid overloading the browser.
  const MAX_PAGES = 5;
  const shouldContinue = vessels.length >= 5000 && !!last_token && last_token !== lastToken && page < MAX_PAGES;

  if (shouldContinue) {
    return fetchVessels(getToken, last_token, allVessels, page + 1);
  }

  // Sort by timestamp (newest first)
  return allVessels.sort((x, y) => getUnixTimestamp(y.timestamp_utc) - getUnixTimestamp(x.timestamp_utc));
}

/**
 * Fetch vessels within a polygon area
 * @param getToken - Function to get authentication token
 * @param polygonWkt - WKT polygon string
 * @returns Promise resolving to array of vessels
 */
export async function fetchVesselsByArea(
  getToken: () => Promise<string>,
  polygonWkt: string,
): Promise<VesselPosition[]> {
  if (!polygonWkt) {
    return [];
  }

  const token = await getToken();

  const today = now();
  const threeDaysAgo = subtractTime(today, 3, 'day');

  const timestamp_utc_start = formatForAPIDate(threeDaysAgo);
  const timestamp_utc_end = formatForAPIDate(today);

  const res = await fetch(`${BASE_URL}${ENDPOINTS.vesselPosition}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      timestamp_utc_start,
      timestamp_utc_end,
      geom: polygonWkt,
    }),
  });

  if (!res.ok) throw new Error(`Failed to fetch vessels by area: ${res.status}`);
  const data: VesselResponse & { errorMessage?: string; errorType?: string } = await res.json();

  // Some backends return a 200 with an error payload. Detect that shape
  // and surface it as an error so React Query can handle retries and
  // the UI can show an appropriate message.
  if (data.errorMessage || data.errorType) {
    const message = data.errorMessage || data.errorType || 'Failed to fetch vessels by area';
    throw new Error(message);
  }

  const results = data.results || data.result || (Array.isArray(data) ? data : []);

  // Sort by timestamp (newest first)
  return results.sort((x, y) => getUnixTimestamp(y.timestamp_utc) - getUnixTimestamp(x.timestamp_utc));
}

/**
 * Fetch a single vessel by MMSI
 * @param getToken - Function to get authentication token
 * @param mmsi - MMSI of the vessel to fetch
 * @returns Promise resolving to the vessel object or null
 */
export async function fetchVesselByMmsi(
  getToken: () => Promise<string>,
  mmsi: number | string,
): Promise<VesselPosition | null> {
  const token = await getToken();
  const url = `${BASE_URL}${ENDPOINTS.vesselPosition}?mmsi=${mmsi}`;

  try {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch vessel by MMSI: ${res.status}`);
    }

    const data: VesselResponse = await res.json();
    const results = data.result || data.results || [];
    return results.length > 0 ? results[0] : null;
  } catch (error) {
    console.error(`Error fetching vessel ${mmsi}:`, error);
    return null;
  }
}
